// CONTRACT:I4-POLICY-DECISION.1.0
// COP — GraphView: the fused multi-INT entity graph, a radial "mission-clock"
// (PDP-gated render surface for CONTRACT:C1-MAP-CLIENT.1.0).
// A clearance-shaped intelligence-fusion instrument. The hero control pair
// (Sadeghi -> MPT) is the central axis; every other entity radiates outward,
// grouped into INT-discipline sectors like hours on a mission clock.
// Corroborating links arc inward toward the spine.
//
// Self-contained: pure React 18 + inline SVG. No npm deps, no Math.random.
// Layout is fully deterministic (seeded from node-id hashes).
// Visibility is decided by the REAL local PDP (decideVisibility) — denied
// nodes/edges are NOT rendered (existence is never revealed).

import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  decideVisibility,
  type PolicyBundle,
  type SubjectClaims,
} from "../../../../packages/abac-pdp/src/index.ts";
import { APP6_AFFILIATION_COLORS } from "../../../../packages/design-tokens/src/index.ts";

/* ====================================================================== */
/* Contract                                                               */
/* ====================================================================== */

export interface GraphViewProps {
  graph: { nodes: any[]; edges: any[] }; // parsed fused-entity-graph.json
  bundle: PolicyBundle; // live policy bundle
  claims: SubjectClaims; // current persona's JWT claims (changes on switch → re-render)
}

/* ====================================================================== */
/* Domain constants                                                       */
/* ====================================================================== */

const HERO_EDGE_ID = "scop:edge:sadeghi-controls-mpt";
const HERO_FROM = "scop:entity:person-sadeghi";
const HERO_TO = "scop:entity:org-mpt";

/** APP-6 affiliation hues — resolved from the design-tokens SINGLE SOURCE
 *  (APP6_AFFILIATION_COLORS), not hand-kept. PRIMITIVE hexes (not var() refs):
 *  GraphView consumes them as raw SVG fill/stroke attributes and `${col}22` alpha
 *  interpolation, where CSS variables would not resolve. The P1 data model uses
 *  "friend"; the token slot is "friendly" — bridge the key (mirrors
 *  affiliationToStatus in design-tokens) so friendly nodes are canon blue, not amber. */
const affilColor = (a: string): string => {
  const key = a === "friend" ? "friendly" : a;
  return (APP6_AFFILIATION_COLORS as Record<string, string>)[key] ?? APP6_AFFILIATION_COLORS.unknown;
};

/** The seven INT disciplines become the clock sectors (radial groups). The order
 *  is deliberate: the human/signals crown-jewel disciplines sit up top near the
 *  spine; the geospatial/measurement disciplines fan out below. */
const DISCIPLINES = ["SIGINT", "HUMINT", "CYBER", "ELINT", "GEOINT", "MASINT", "OSINT"] as const;
type Discipline = (typeof DISCIPLINES)[number];

/** A faint per-sector tint (used only for the sector label + spoke, never as an
 *  affiliation signal — affiliation owns the saturated hues). */
const SECTOR_TINT: Record<Discipline, string> = {
  SIGINT: "#7Fb0d6",
  HUMINT: "#c9a06a",
  CYBER: "#9d8fd6",
  ELINT: "#6fc6b8",
  GEOINT: "#8fb37a",
  MASINT: "#c98fb0",
  OSINT: "#9aa6b0",
};

/* ====================================================================== */
/* Deterministic helpers (NO Math.random)                                 */
/* ====================================================================== */

/** FNV-1a 32-bit hash of a string → stable unsigned int. Seeds all jitter. */
function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
/** Stable pseudo-random in [0,1) from an id + salt. Deterministic per node. */
function rand01(id: string, salt: number): number {
  return (hash32(`${id}#${salt}`) % 100000) / 100000;
}

/* ====================================================================== */
/* PDP visibility (the REAL decision — no faked filtering)                */
/* ====================================================================== */

interface VizNode {
  id: string;
  label: string;
  node_type: string;
  affiliation: string;
  classification: string;
  protectedBadge: boolean;
  int_coverage: Discipline[];
  primary: Discipline; // sector assignment
  note?: string;
}

interface VizEdge {
  id: string;
  from: string;
  to: string;
  relation: string;
  corroborated: boolean;
  intCount: number;
  disciplines: Discipline[];
  isHero: boolean;
  ownSecurity: boolean;
  classification: string;
}

const RANK: Record<string, number> = {
  UNCLASSIFIED: 0,
  CONFIDENTIAL: 1,
  SECRET: 2,
  TOP_SECRET: 3,
};

/** Max-of-two security blocks (a link is at least as protected as its endpoints). */
function maxSecurity(a: any, b: any): any {
  const ra = RANK[a?.classification] ?? 0;
  const rb = RANK[b?.classification] ?? 0;
  const hi = ra >= rb ? a : b;
  // mission/caveats UNION (a link requires every compartment/caveat of both ends);
  // releasability INTERSECTION (releasable only to those BOTH ends are — most restrictive).
  const uniq = (xs: string[] = [], ys: string[] = []) => Array.from(new Set([...xs, ...ys]));
  const intersect = (xs: string[] = [], ys: string[] = []) => xs.filter((x) => ys.includes(x));
  return {
    classification: hi?.classification ?? "UNCLASSIFIED",
    releasability: intersect(a?.releasability, b?.releasability),
    mission: uniq(a?.mission, b?.mission),
    caveats: uniq(a?.caveats, b?.caveats),
  };
}

/** Pick a node's clock-sector discipline: its first int_coverage entry that is a
 *  known discipline, falling back to OSINT (the always-present baseline). */
function primaryDiscipline(coverage: string[]): Discipline {
  for (const c of coverage ?? []) {
    if ((DISCIPLINES as readonly string[]).includes(c)) return c as Discipline;
  }
  return "OSINT";
}

/** Resolve the full visible picture under the current claims. Pure. */
function resolveVisible(
  graph: { nodes: any[]; edges: any[] },
  bundle: PolicyBundle,
  claims: SubjectClaims,
): { nodes: VizNode[]; edges: VizEdge[]; visibleIds: Set<string> } {
  const byId = new Map<string, any>(graph.nodes.map((n) => [n.id, n]));
  const visibleIds = new Set<string>();
  const nodes: VizNode[] = [];

  for (const n of graph.nodes) {
    const { presentation } = decideVisibility(n.security ?? {}, bundle, claims);
    if (!presentation.visible) continue; // hidden = NOT rendered
    visibleIds.add(n.id);
    nodes.push({
      id: n.id,
      label: n.label,
      node_type: n.node_type,
      affiliation: n.affiliation,
      classification: n.security?.classification ?? "UNCLASSIFIED",
      protectedBadge: presentation.protectedBadge,
      int_coverage: (n.int_coverage ?? []) as Discipline[],
      primary: primaryDiscipline(n.int_coverage ?? []),
      note: n.note,
    });
  }

  const edges: VizEdge[] = [];
  for (const e of graph.edges) {
    if (!visibleIds.has(e.from) || !visibleIds.has(e.to)) continue; // both endpoints required
    const from = byId.get(e.from);
    const to = byId.get(e.to);
    const sec = e.security ?? maxSecurity(from?.security, to?.security);
    const { presentation } = decideVisibility(sec, bundle, claims);
    if (!presentation.visible) continue;
    edges.push({
      id: e.id,
      from: e.from,
      to: e.to,
      relation: e.relation,
      corroborated: !!e.multi_int_corroborated,
      intCount: (e.int_disciplines ?? []).length,
      disciplines: (e.int_disciplines ?? []) as Discipline[],
      isHero: e.id === HERO_EDGE_ID,
      ownSecurity: !!e.security,
      classification: sec?.classification ?? "UNCLASSIFIED",
    });
  }

  return { nodes, edges, visibleIds };
}

/* ====================================================================== */
/* Deterministic radial layout                                            */
/* ====================================================================== */

const VIEW_W = 1100;
const VIEW_H = 760;
const CX = VIEW_W / 2;
const CY = VIEW_H / 2;

interface Placed {
  x: number;
  y: number;
  ring: number; // 0 = hero spine, 1..n = radiating rings
}

/** Place the visible nodes. The hero pair anchors the vertical spine through
 *  center; everything else is bucketed into discipline sectors and laid on
 *  concentric rings within its angular wedge. Fully deterministic. */
function layout(nodes: VizNode[]): Map<string, Placed> {
  const pos = new Map<string, Placed>();

  // --- Hero spine: Sadeghi above center, MPT below. The control axis. ---
  const SPINE = 86; // half-distance of the hero pair from center
  const hasSadeghi = nodes.some((n) => n.id === HERO_FROM);
  const hasMpt = nodes.some((n) => n.id === HERO_TO);
  if (hasSadeghi) pos.set(HERO_FROM, { x: CX, y: CY - SPINE, ring: 0 });
  if (hasMpt) pos.set(HERO_TO, { x: CX, y: CY + SPINE, ring: 0 });

  // --- Sector geometry. Seven wedges around the clock. The top wedge (12
  // o'clock) is reserved as a thin gutter so the hero spine reads cleanly; the
  // discipline wedges fill the remaining 360°, biased to avoid the vertical axis.
  const others = nodes.filter((n) => n.id !== HERO_FROM && n.id !== HERO_TO);

  // Bucket by primary discipline, preserving deterministic order.
  const buckets = new Map<Discipline, VizNode[]>();
  for (const d of DISCIPLINES) buckets.set(d, []);
  for (const n of others) buckets.get(n.primary)!.push(n);
  // Deterministic intra-bucket order: by id hash (stable, input-independent).
  for (const d of DISCIPLINES) {
    buckets.get(d)!.sort((a, b) => hash32(a.id) - hash32(b.id));
  }

  const N = DISCIPLINES.length;
  // Each discipline owns a wedge centred on its clock angle. Start at -90°+offset
  // so SIGINT sits up-left of the spine and sectors sweep clockwise.
  const RING0 = 188; // first ring radius
  const RING_STEP = 92; // radial spacing between rings
  const PER_RING = 5; // nodes per ring before spilling outward

  DISCIPLINES.forEach((disc, si) => {
    const bucket = buckets.get(disc)!;
    if (bucket.length === 0) return;
    // Wedge centre angle. Rotate so wedges straddle the clock evenly, nudged
    // by +12° so no wedge centre lands exactly on the vertical hero axis.
    const wedgeCenter = (si / N) * Math.PI * 2 - Math.PI / 2 + 0.21;
    const wedgeHalf = (Math.PI / N) * 0.82; // leave hairline gaps between sectors

    bucket.forEach((n, i) => {
      const ring = Math.floor(i / PER_RING) + 1;
      const idxInRing = i % PER_RING;
      const countInRing = Math.min(PER_RING, bucket.length - (ring - 1) * PER_RING);
      // Spread nodes across the wedge; single node sits dead-centre.
      const t = countInRing === 1 ? 0.5 : idxInRing / (countInRing - 1);
      const angle = wedgeCenter - wedgeHalf + t * (2 * wedgeHalf);
      // Deterministic radial jitter so rings don't look mechanical.
      const jitter = (rand01(n.id, ring) - 0.5) * 26;
      const r = RING0 + (ring - 1) * RING_STEP + jitter;
      // Squash vertically so the wide aspect ratio is filled (ellipse, not circle).
      pos.set(n.id, {
        x: CX + Math.cos(angle) * r * 1.12,
        y: CY + Math.sin(angle) * r * 0.82,
        ring,
      });
    });
  });

  return pos;
}

/* ====================================================================== */
/* Geometry helpers                                                       */
/* ====================================================================== */

/** Quadratic-bezier path that arcs INWARD toward the spine (the visual grammar:
 *  corroborating records bend toward the central axis). */
function arcPath(x1: number, y1: number, x2: number, y2: number, bow: number): string {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  // Control point pulled toward the centre (spine), proportional to bow.
  const cx = mx + (CX - mx) * bow;
  const cy = my + (CY - my) * bow;
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`;
}

/** Glyph path for a node_type, centred at origin, radius r. Pure SVG. */
function nodeGlyph(type: string, r: number): JSX.Element {
  switch (type) {
    case "person":
      // diamond
      return <path d={`M 0 ${-r} L ${r} 0 L 0 ${r} L ${-r} 0 Z`} />;
    case "organization":
      // square
      return <rect x={-r * 0.82} y={-r * 0.82} width={r * 1.64} height={r * 1.64} rx={1.5} />;
    case "facility":
      // up-triangle
      return <path d={`M 0 ${-r} L ${r * 0.92} ${r * 0.7} L ${-r * 0.92} ${r * 0.7} Z`} />;
    case "track":
      // chevron / wedge (moving)
      return <path d={`M 0 ${-r} L ${r * 0.85} ${r * 0.85} L 0 ${r * 0.35} L ${-r * 0.85} ${r * 0.85} Z`} />;
    case "observation":
      // hexagon
      return (
        <path
          d={(() => {
            const pts: string[] = [];
            for (let k = 0; k < 6; k++) {
              const a = (k / 6) * Math.PI * 2 - Math.PI / 2;
              pts.push(`${(Math.cos(a) * r).toFixed(1)} ${(Math.sin(a) * r).toFixed(1)}`);
            }
            return `M ${pts.join(" L ")} Z`;
          })()}
        />
      );
    default:
      return <circle r={r} />;
  }
}

/* ====================================================================== */
/* The component                                                          */
/* ====================================================================== */

export function GraphView(props: GraphViewProps): JSX.Element {
  const { graph, bundle, claims } = props;

  // The visible picture is a pure function of (graph, bundle, claims).
  // Recomputing on claims change is what re-shapes the instrument.
  const { nodes, edges, visibleIds } = useMemo(
    () => resolveVisible(graph, bundle, claims),
    [graph, bundle, claims],
  );

  // Layout depends only on which nodes are visible (deterministic).
  const visKey = useMemo(() => nodes.map((n) => n.id).sort().join("|"), [nodes]);
  const pos = useMemo(() => layout(nodes), [visKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Hero state: is the crown-jewel control link assembled right now? ---
  const heroVisible = edges.some((e) => e.isHero);
  const heroEndpointsVisible = visibleIds.has(HERO_FROM) && visibleIds.has(HERO_TO);
  // Hero marking + corroboration sourced from the data (so it cannot drift from the PDP).
  const heroEdgeData = useMemo(() => (graph.edges as any[]).find((e) => e.id === HERO_EDGE_ID), [graph]);
  const heroMarking = useMemo(() => formatMarking(heroEdgeData?.security), [heroEdgeData]);
  const heroCorrob = (heroEdgeData?.int_disciplines ?? []).length;

  // Animation driver: a 0→1 "seal" progress that re-runs whenever the hero
  // state flips on a claims change. When the edge assembles it draws + seals;
  // when it seals shut it dissolves. Seeded by a claims signature so persona
  // switches retrigger it.
  const claimsSig = useMemo(() => JSON.stringify(claims), [claims]);
  const [seal, setSeal] = useState(0);
  const sealRef = useRef(0); // latest seal, so a retrigger animates from the current value
  const rafRef = useRef<number | null>(null);
  const prefersReduced =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (prefersReduced) {
      setSeal(heroVisible ? 1 : 0);
      return;
    }
    const target = heroVisible ? 1 : 0;
    const start = performance.now();
    const from = sealRef.current;
    const DUR = 1100;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DUR);
      // easeInOutCubic
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const v = from + (target - from) * e;
      setSeal(v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // Retrigger on persona switch (claimsSig) and hero state change.
  }, [claimsSig, heroVisible, prefersReduced]);

  // Keep the ref in sync with the latest seal (declared above the animation effect).
  useEffect(() => {
    sealRef.current = seal;
  }, [seal]);

  const [hover, setHover] = useState<string | null>(null);

  // Pre-resolve node positions for edge drawing.
  const edgeLines = edges
    .map((e) => {
      const a = pos.get(e.from);
      const b = pos.get(e.to);
      if (!a || !b) return null;
      return { e, a, b };
    })
    .filter((x): x is { e: VizEdge; a: Placed; b: Placed } => x !== null);

  // Visible-node counts by affiliation for the legend tallies.
  const tally: Record<string, number> = {};
  for (const n of nodes) tally[n.affiliation] = (tally[n.affiliation] ?? 0) + 1;

  // NOTE (DESIGN-SPEC §6 denial test): a withheld link must read IDENTICALLY to a
  // never-existed one — no padlock, no marking, no "sealed" label between two visible
  // nodes (that is the OpenCTI neighbor-implies-link + history-name leak). So the
  // under-cleared viewer sees the same neutral "no attributed control in view" as a
  // fully-absent hero; the link simply does not render. (heroEndpointsVisible still
  // drives the SUBJECT/FRONT captions, which are node-level, not link-level.)
  return (
    <div className="radclock" style={ROOT_STYLE}>
      <style>{CSS}</style>

      {/* ---- Header strip ---- */}
      <div className="radclock__head">
        <div className="radclock__title">
          FUSION GRAPH · MISSION CLOCK
          <span className="radclock__sub">Op HIGH MAST — multi-INT entity fusion</span>
        </div>
        <div className="radclock__counts">
          <span className="radclock__count">
            {nodes.length}
            <em>entities</em>
          </span>
          <span className="radclock__count">
            {edges.length}
            <em>links</em>
          </span>
          <span
            className="radclock__hero-state"
            data-state={heroVisible ? "assembled" : "absent"}
          >
            {heroVisible ? "● CONTROL ATTRIBUTED" : "○ NO ATTRIBUTED CONTROL IN VIEW"}
          </span>
        </div>
      </div>

      {/* ---- The instrument ---- */}
      <svg
        className="radclock__svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Radial mission-clock fusion graph"
      >
        <defs>
          <radialGradient id="rc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(31,182,182,0.16)" />
            <stop offset="100%" stopColor="rgba(31,182,182,0)" />
          </radialGradient>
          <filter id="rc-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Concentric ring guides + sector spokes (the clock face) */}
        <g className="rc-face" opacity={0.55}>
          {[1, 2, 3].map((ring) => (
            <ellipse
              key={ring}
              cx={CX}
              cy={CY}
              rx={(188 + (ring - 1) * 92) * 1.12}
              ry={(188 + (ring - 1) * 92) * 0.82}
              fill="none"
              stroke="var(--ui-hairline-soft, rgba(216,222,230,0.07))"
              strokeWidth={1}
            />
          ))}
          {DISCIPLINES.map((disc, si) => {
            const N = DISCIPLINES.length;
            const a = (si / N) * Math.PI * 2 - Math.PI / 2 + 0.21;
            const rr = 432;
            const lx = CX + Math.cos(a) * rr * 1.12;
            const ly = CY + Math.sin(a) * rr * 0.82;
            return (
              <g key={disc}>
                <line
                  x1={CX}
                  y1={CY}
                  x2={lx}
                  y2={ly}
                  stroke="var(--ui-hairline-soft, rgba(216,222,230,0.06))"
                  strokeWidth={1}
                  strokeDasharray="2 6"
                />
                <text
                  x={lx}
                  y={ly}
                  className="rc-sector-label"
                  fill={SECTOR_TINT[disc]}
                  textAnchor={Math.cos(a) < -0.2 ? "end" : Math.cos(a) > 0.2 ? "start" : "middle"}
                >
                  {disc}
                </text>
              </g>
            );
          })}
        </g>

        {/* Soft core glow under the hero spine */}
        <circle cx={CX} cy={CY} r={150} fill="url(#rc-core)" />

        {/* ---- Edges (corroborating arcs first, hero last/on top) ---- */}
        <g className="rc-edges">
          {edgeLines
            .filter(({ e }) => !e.isHero)
            .map(({ e, a, b }) => {
              const col = e.ownSecurity ? "var(--ui-accent, #1fb6b6)" : "rgba(150,166,180,0.5)";
              const w = 0.8 + Math.min(e.intCount, 5) * 0.42; // thickness ∝ corroboration
              const dash = e.corroborated ? undefined : "3 4";
              const active = hover && (e.from === hover || e.to === hover);
              return (
                <path
                  key={e.id}
                  d={arcPath(a.x, a.y, b.x, b.y, 0.32)}
                  fill="none"
                  stroke={col}
                  strokeWidth={active ? w + 1 : w}
                  strokeDasharray={dash}
                  strokeLinecap="round"
                  opacity={hover ? (active ? 0.95 : 0.12) : e.ownSecurity ? 0.7 : 0.42}
                  className="rc-edge"
                />
              );
            })}
        </g>

        {/* ---- The HERO control axis (assembling / sealing) ---- */}
        <HeroAxis
          from={pos.get(HERO_FROM)}
          to={pos.get(HERO_TO)}
          seal={seal}
          present={heroVisible}
          endpointsVisible={heroEndpointsVisible}
          marking={heroMarking}
          corrob={heroCorrob}
        />

        {/* ---- Nodes ---- */}
        <g className="rc-nodes">
          {nodes.map((n) => {
            const p = pos.get(n.id);
            if (!p) return null;
            const isHeroNode = n.id === HERO_FROM || n.id === HERO_TO;
            const r = isHeroNode ? 11 : p.ring === 1 ? 8.5 : 7;
            const col = affilColor(n.affiliation);
            const dim = hover && hover !== n.id && !isNeighbor(hover, n.id, edges);
            return (
              <g
                key={n.id}
                transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})`}
                className="rc-node"
                opacity={dim ? 0.22 : 1}
                onMouseEnter={() => setHover(n.id)}
                onMouseLeave={() => setHover(null)}
              >
                {/* affiliation ring */}
                <g
                  fill={isHeroNode ? col : `${col}22`}
                  stroke={col}
                  strokeWidth={isHeroNode ? 2 : 1.5}
                  filter={isHeroNode ? "url(#rc-glow)" : undefined}
                >
                  {nodeGlyph(n.node_type, r)}
                </g>
                {/* DESIGN-SPEC §2.4: no resident padlock-analog. A visible node is just
                    visible; its TDF-protected status lives in the inspector on select,
                    not as an always-on graph glyph (security invisible at rest). */}
                {/* label — only hero pair + hovered + ring-1 to stay legible */}
                {(isHeroNode || hover === n.id || p.ring === 1) && (
                  <text
                    className={isHeroNode ? "rc-label rc-label--hero" : "rc-label"}
                    x={0}
                    y={r + 12}
                    textAnchor="middle"
                  >
                    {shortLabel(n.label)}
                  </text>
                )}
              </g>
            );
          })}
        </g>

        {/* Hero spine endpoint captions (always when both present) */}
        {heroEndpointsVisible && (
          <g className="rc-spine-caps">
            <text x={CX} y={CY - 86 - 26} textAnchor="middle" className="rc-spine-cap">
              SUBJECT
            </text>
            <text x={CX} y={CY + 86 + 34} textAnchor="middle" className="rc-spine-cap">
              FRONT
            </text>
          </g>
        )}
      </svg>

      {/* ---- Legend / readout ---- */}
      <div className="radclock__legend">
        <div className="rc-leg-group">
          {(["hostile", "friend", "neutral", "unknown", "pending"] as const).map((a) => (
            <span className="rc-leg" key={a}>
              <i style={{ background: affilColor(a) }} />
              {a}
              {tally[a] ? <b>{tally[a]}</b> : null}
            </span>
          ))}
        </div>
        <div className="rc-leg-group rc-leg-group--right">
          <span className="rc-leg rc-leg--edge">
            <i className="rc-leg-edge rc-leg-edge--prot" /> per-edge ABAC
          </span>
          <span className="rc-leg rc-leg--edge">
            <i className="rc-leg-edge" /> inherited
          </span>
          <span className="rc-leg rc-leg--edge">
            <i className="rc-leg-edge rc-leg-edge--dash" /> single-INT
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Hero axis sub-component — the money shot                               */
/* ---------------------------------------------------------------------- */

function HeroAxis(props: {
  from?: Placed;
  to?: Placed;
  seal: number; // 0..1 assembly progress
  present: boolean; // edge currently permitted
  endpointsVisible: boolean;
  marking: string; // sourced from the hero edge's security (not hardcoded)
  corrob: number; // int_disciplines count
}): JSX.Element | null {
  const { from, to, seal, present, marking, corrob } = props;
  if (!from || !to) return null;

  const x1 = from.x;
  const y1 = from.y;
  const x2 = to.x;
  const y2 = to.y;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  // DESIGN-SPEC §6 denial test: a withheld control link renders NOTHING — pixel-
  // identical to a link that never existed. No stub-lines, no padlock body, and (the
  // live OpenCTI-class leak this removes) no marking printed between two visible
  // endpoints. The endpoints stay visible as nodes; only the privileged EDGE is
  // absent. When assembling/assembled the spine draws and the seal medallion locks
  // at center (below).
  if (!present) return null;

  // ASSEMBLED / ASSEMBLING state. The control axis is drawn with a stroke-dash
  // reveal proportional to `seal`, glowing teal (the secure spine). A seal
  // medallion fades/scales in at center as it completes.
  const drawn = Math.max(0.001, seal);
  const sealIn = Math.max(0, (seal - 0.55) / 0.45); // medallion appears in the back half

  return (
    <g className="rc-hero rc-hero--assembled" pointerEvents="none">
      {/* outer glow underlay */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="rgba(31,182,182,0.22)"
        strokeWidth={9}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={1 - drawn}
        filter="url(#rc-glow)"
      />
      {/* core control line */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="var(--ui-accent, #1fb6b6)"
        strokeWidth={2.4}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={1 - drawn}
      />
      {/* directional ticks marching toward MPT (control flows down) */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#fff"
        strokeWidth={1}
        strokeLinecap="round"
        opacity={0.5 * sealIn}
        pathLength={1}
        strokeDasharray="0.02 0.05"
        strokeDashoffset={1 - drawn}
      />
      {/* the SEAL medallion — locks the attribution */}
      <g
        transform={`translate(${midX} ${midY}) scale(${0.7 + 0.3 * sealIn})`}
        opacity={sealIn}
        className="rc-seal-medallion"
      >
        <circle r={15} fill="#06120e" stroke="var(--ui-accent,#1fb6b6)" strokeWidth={1.6} />
        <circle r={15} fill="none" stroke="var(--ui-accent,#1fb6b6)" strokeWidth={0.6} opacity={0.5} transform="scale(1.35)" />
        {/* closed clasp = sealed/attributed */}
        <rect x={-6} y={-2.5} width={12} height={9.5} rx={1.8} fill="none" stroke="var(--ui-accent,#1fb6b6)" strokeWidth={1.4} />
        <path d="M -3.5 -2.5 V -6 a 3.5 3.5 0 0 1 7 0 V -2.5" fill="none" stroke="var(--ui-accent,#1fb6b6)" strokeWidth={1.4} />
      </g>
      <text x={midX + 22} y={midY - 6} className="rc-hero-tag" opacity={sealIn}>
        ULTIMATE BENEFICIAL CONTROL
      </text>
      <text x={midX + 22} y={midY + 7} className="rc-hero-tag rc-hero-tag--dim" opacity={sealIn}>
        {marking} · {corrob}-INT corroborated
      </text>
    </g>
  );
}

/* ====================================================================== */
/* Small pure utilities                                                   */
/* ====================================================================== */

function isNeighbor(a: string, b: string, edges: VizEdge[]): boolean {
  return edges.some((e) => (e.from === a && e.to === b) || (e.from === b && e.to === a));
}

const CLASS_ABBR: Record<string, string> = { TOP_SECRET: "TS", SECRET: "S", CONFIDENTIAL: "C", CUI: "CUI", UNCLASSIFIED: "U" };
/** Render a security block as a compact banner marking, sourced from the data
 *  (never hardcoded) — e.g. TS//SI/SI-G//NOFORN//ORCON. */
function formatMarking(sec: any): string {
  if (!sec) return "";
  const cls = CLASS_ABBR[sec.classification] ?? sec.classification ?? "";
  const comps = ((sec.mission ?? []) as string[]).map((m) => m.replace(/^[a-z]+:/i, "")).filter(Boolean);
  const cavs = (sec.caveats ?? []) as string[];
  return [cls, comps.join("/"), cavs.join("//")].filter(Boolean).join("//");
}

/** Trim long dataset labels to the leading proper-noun chunk for on-graph use. */
function shortLabel(label: string): string {
  // Strip parenthetical asides, then cap length.
  const base = label.replace(/\s*\(.*?\)\s*/g, " ").trim();
  return base.length > 22 ? base.slice(0, 21) + "…" : base;
}

/* ====================================================================== */
/* Style (dark tactical, design-token driven)                             */
/* ====================================================================== */

const ROOT_STYLE: React.CSSProperties = {
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: "var(--color-surface-base, #06090D)",
  color: "var(--ui-text, #d8dee6)",
  fontFamily: "var(--typography-family-mono, ui-monospace, SFMono-Regular, monospace)",
  overflow: "hidden",
};

const CSS = `
.radclock { --rc-spine: var(--ui-accent, #1fb6b6); }
.radclock__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 10px 16px 8px; border-bottom: 1px solid var(--ui-hairline, rgba(31,182,182,0.14));
  flex: 0 0 auto;
}
.radclock__title {
  font-size: 12px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--rc-spine);
  display: flex; flex-direction: column; gap: 3px;
}
.radclock__sub { font-size: 10px; letter-spacing: 0.08em; color: var(--ui-text-faint, #5a6571); text-transform: none; }
.radclock__counts { display: flex; align-items: center; gap: 16px; }
.radclock__count { font-size: 18px; color: var(--ui-text, #d8dee6); display: flex; align-items: baseline; gap: 5px; font-variant-numeric: tabular-nums; }
.radclock__count em { font-style: normal; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ui-text-faint, #5a6571); }
.radclock__hero-state {
  font-size: 10px; letter-spacing: 0.12em; padding: 3px 9px; border-radius: 2px;
  border: 1px solid var(--ui-hairline, rgba(31,182,182,0.18));
}
.radclock__hero-state[data-state="assembled"] { color: var(--rc-spine); border-color: rgba(31,182,182,0.5); background: rgba(31,182,182,0.08); }
.radclock__hero-state[data-state="absent"] { color: var(--ui-text-faint, #5a6571); }

.radclock__svg { flex: 1 1 auto; min-height: 0; width: 100%; display: block; }

.rc-sector-label { font-size: 9.5px; letter-spacing: 0.18em; opacity: 0.65; }
.rc-edge { transition: opacity 140ms ease, stroke-width 140ms ease; }
.rc-node { transition: opacity 160ms ease; cursor: default; }
.rc-lock { font-size: 9px; }
.rc-label { font-size: 9px; fill: var(--ui-text-dim, #8b96a3); letter-spacing: 0.04em; paint-order: stroke; stroke: var(--color-surface-base, #06090D); stroke-width: 2.5px; }
.rc-label--hero { font-size: 10.5px; fill: var(--ui-text, #d8dee6); letter-spacing: 0.06em; font-weight: 600; }
.rc-spine-cap { font-size: 8.5px; letter-spacing: 0.22em; fill: var(--ui-text-faint, #5a6571); }
.rc-hero-tag { font-size: 9.5px; letter-spacing: 0.1em; fill: var(--rc-spine); paint-order: stroke; stroke: var(--color-surface-base,#06090D); stroke-width: 3px; }
.rc-hero-tag--dim { fill: var(--ui-text-dim, #8b96a3); font-size: 8.5px; letter-spacing: 0.06em; }
.rc-seal-lock { animation: rc-pulse 2.4s ease-in-out infinite; }
@keyframes rc-pulse { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }

.radclock__legend {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 7px 16px; border-top: 1px solid var(--ui-hairline, rgba(31,182,182,0.14));
  flex-wrap: wrap;
}
.rc-leg-group { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.rc-leg { display: inline-flex; align-items: center; gap: 5px; font-size: 9.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ui-text-dim, #8b96a3); }
.rc-leg i { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
.rc-leg b { font-weight: 600; color: var(--ui-text, #d8dee6); font-variant-numeric: tabular-nums; margin-left: 1px; }
.rc-leg-edge { width: 16px !important; height: 2px !important; border-radius: 0 !important; background: rgba(150,166,180,0.6); }
.rc-leg-edge--prot { background: var(--rc-spine); height: 2.5px !important; }
.rc-leg-edge--dash { background: repeating-linear-gradient(90deg, rgba(150,166,180,0.7) 0 3px, transparent 3px 6px); }

@media (prefers-reduced-motion: reduce) {
  .rc-edge, .rc-node, .rc-seal-lock { transition: none; animation: none; }
}
`;

export default GraphView;
